import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { ChevronLeft, ChevronRight, LayoutGrid, Download, UploadCloud } from 'lucide-react';
import { Button, Heading, Text, Select } from '../../../components';
import { ScheduleSkeleton } from '../../../skeletons';
import { domiciliaryScheduleService } from '../../../services';
import { localDateKey, todayKey } from '../../../utils/datetime';
import { carersFromSchedule, computeWindow, collectReviews, reviewCountForZone, visitReviewKind, assignBlockReason } from './scheduleUtils';
import { applyScheduleDraft } from './applyScheduleDraft';
import {
  enqueueChange as enqueueChangeAction,
  removeChange,
  setChanges,
  clearChanges,
} from '../../../store/domiciliaryScheduleDraftSlice';

import DateSelector from './DateSelector';
import ScheduleToolbar from './ScheduleToolbar';
import UnassignedVisits from './UnassignedVisits';
import ZoneRoster from './ZoneRoster';
import AssignPackageModal from './AssignPackageModal';
import VisitDetailsModal from './VisitDetailsModal';
import PackageFormModal from './PackageFormModal';
import CreateZoneModal from './CreateZoneModal';
import TimeAwayModal from './TimeAwayModal';
import AvailabilityModal from './AvailabilityModal';
import ExportPayrollModal from './ExportPayrollModal';
import PublishReviewModal from './PublishReviewModal';
import ReviewListModal from './ReviewListModal';
import VisitReviewModal from './VisitReviewModal';
import CarerStatusModal from './CarerStatusModal';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Monday-anchored week containing `dateStr`.
const weekOf = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  const dow = base.getDay();
  const monday = new Date(base);
  monday.setDate(base.getDate() - dow + (dow === 0 ? -6 : 1));
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(monday);
    x.setDate(monday.getDate() + i);
    return x;
  });
};

/**
 * Zone-wise daily schedule. One API call returns the whole screen already
 * resolved; this component renders the SERVER snapshot with the unpublished
 * draft queue (Redux) applied on top. Schedule edits stage as draft changes —
 * nothing hits the API until Publish replays the queue (mirrors the duty
 * roster). Layout mirrors the duty roster: a compact sticky header tucked into
 * the dashboard's padding, then the board.
 */
const ScheduleScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const changes = useSelector((s) => s.domiciliaryScheduleDraft.changes);

  const [date, setDate] = useState(todayKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [zoneFilter, setZoneFilter] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);
  const [assignVisit, setAssignVisit] = useState(null);
  const [detailVisit, setDetailVisit] = useState(null);
  const [formState, setFormState] = useState(null); // { packageId, fromDate } | null
  const [zoneModal, setZoneModal] = useState(null);
  const [timeAwayOpen, setTimeAwayOpen] = useState(false);
  const [timeAwayEdit, setTimeAwayEdit] = useState(null); // existing booking being edited
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [availabilityCarerId, setAvailabilityCarerId] = useState(null); // prefill when opened from a carer
  const [availabilityWindow, setAvailabilityWindow] = useState(null);   // {start,end} prefill from the carer's timeline band
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reviewScope, setReviewScope] = useState(null); // { zone } | null  (review queue)
  const [reviewItem, setReviewItem] = useState(null);    // item being resolved
  const [carerStatus, setCarerStatus] = useState(null);  // { carer, loading, data } | null
  const [dragVisit, setDragVisit] = useState(null);      // unassigned visit being dragged onto a carer

  const fetchSchedule = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await domiciliaryScheduleService.getSchedule(d);
      if (res.success) setData(res.data);
    } catch (e) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSchedule(date); }, [date, fetchSchedule]);

  // Auto-refresh every 50s so the "now" time marker and any new clock-ins stay
  // current without the manager manually reloading the page. Unpublished draft
  // edits are reapplied on top of the fresh snapshot, so nothing is lost.
  // Paused while publishing to avoid clobbering the in-flight sync.
  useEffect(() => {
    const id = setInterval(() => { if (!publishing) fetchSchedule(date); }, 50000);
    return () => clearInterval(id);
  }, [date, fetchSchedule, publishing]);

  const refresh = () => fetchSchedule(date);

  // The board shows the saved snapshot with unpublished edits applied on top.
  const viewData = useMemo(() => applyScheduleDraft(data, changes), [data, changes]);
  const carers = useMemo(() => carersFromSchedule(viewData?.zones || []), [viewData]);
  const win = useMemo(() => computeWindow(viewData?.zones || [], viewData?.unassigned || []), [viewData]);
  const reviews = useMemo(() => collectReviews(viewData?.zones || []), [viewData]);
  const weekDates = useMemo(() => weekOf(date), [date]);

  // ── Draft queue helpers ───────────────────────────────────────────────────
  const draftId = () => `dchg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const enqueue = (change) => dispatch(enqueueChangeAction({ id: draftId(), ...change }));
  const removeOneChange = (id) => dispatch(removeChange(id));

  // Stage a carer assignment (from the Assign popup).
  const stageAssign = (carerIds, scope) => {
    if (!assignVisit) return;
    const v = assignVisit;
    const names = carerIds.map((c) => c.name).join(' & ');
    enqueue({
      type: 'assign',
      dedupKey: `assign:${v.assignment_id}:${date}`,
      payload: { assignment_id: v.assignment_id, date, carerIds, scope, visit: v },
      label: carerIds.length === 0
        ? `Unassign ${v.resident?.name || v.package_name} · ${v.start}`
        : `${v.resident?.name || v.package_name} · ${v.start} → ${names} (${scope === 'always' ? 'every day' : 'this visit'})`,
    });
  };

  // Drag-and-drop assign: drop an unassigned visit onto a carer row. We run a
  // few sanity checks first and block (with a warning) when the carer can't take
  // the visit — use the Assign popup to override these. Otherwise stage a
  // per-day ("this visit") assignment.
  const assignByDrop = (visit, carer) => {
    if (!visit || !carer) return;

    // Block (with a warning) when the carer is on leave, already on this visit,
    // or double-booked at the same time. Use the Assign popup to override.
    const reason = assignBlockReason(carer, visit);
    if (reason) {
      toast.error(reason);
      return;
    }

    const carerIds = [{ id: carer.id, name: carer.name }];
    enqueue({
      type: 'assign',
      dedupKey: `assign:${visit.assignment_id}:${date}`,
      payload: { assignment_id: visit.assignment_id, date, carerIds, scope: 'today', visit },
      label: `${visit.resident?.name || visit.package_name} · ${visit.start} → ${carer.name} (this visit)`,
    });
    toast.success(`Assigned to ${carer.name}`);
    setDragVisit(null);
  };

  // Stage a new / updated time-away booking. Editing a saved booking removes
  // the old one then re-creates it so the replay ends with one entry.
  const stageTimeAway = ({ employee_id, name, leave_type, reason, notes, existingId, date: leaveDate }) => {
    const d = leaveDate || date; // defaults to the current/selected date
    if (existingId) {
      enqueue({
        type: 'removeTimeAway',
        dedupKey: `removeTimeAway:${existingId}`,
        payload: { existingId, employee_id, name },
        label: `Replace previous time away · ${name}`,
      });
    }
    enqueue({
      type: 'timeAway',
      dedupKey: `timeAway:${employee_id}:${d}`,
      payload: { employee_id, name, date: d, start_date: d, end_date: d, leave_type, reason, notes },
      label: `${reason} · ${name} · ${d}`,
    });
  };

  // Delete a time-away booking. Saved bookings stage a removal; a draft-only
  // booking just drops its queued change.
  const stageRemoveTimeAway = ({ existingId, employee_id, name }) => {
    if (existingId) {
      enqueue({
        type: 'removeTimeAway',
        dedupKey: `removeTimeAway:${existingId}`,
        payload: { existingId, employee_id, name },
        label: `Remove time away · ${name} · ${date}`,
      });
    } else {
      const queued = changes.find((c) => c.dedupKey === `timeAway:${employee_id}:${date}`);
      if (queued) removeOneChange(queued.id);
    }
  };

  const stageAvailability = ({ employee_id, name, zone_id, start, end, note }) => {
    enqueue({
      type: 'availability',
      dedupKey: `availability:${employee_id}:${date}:${start}`,
      payload: { employee_id, name, zone_id, date, start, end, note },
      label: `Extra availability · ${name} · ${start}–${end}`,
    });
  };

  // Remove the carer from a single day's visit (per-day override → null carer).
  const stageUnassign = (visit) => {
    if (!visit) return;
    enqueue({
      type: 'assign',
      dedupKey: `assign:${visit.assignment_id}:${date}`,
      payload: { assignment_id: visit.assignment_id, date, carerIds: [], scope: 'today', visit },
      label: `Unassign ${visit.resident?.name || visit.package_name} · ${visit.start}`,
    });
    toast.success('Unassign added to draft');
  };

  const stageStop = (assignmentId, ctx = {}) => {
    enqueue({
      type: 'stopVisit',
      dedupKey: `stopVisit:${assignmentId}:${date}`,
      payload: { assignment_id: assignmentId, date },
      label: `Stop ${ctx.name || 'package'} from ${date}`,
    });
  };

  // Replay one queued change against the API.
  const syncChange = (c) => {
    const p = c.payload;
    switch (c.type) {
      case 'assign':
        return domiciliaryScheduleService.assignCarer({
          assignment_id: p.assignment_id, date: p.date, carer_ids: p.carerIds.map((x) => x.id), scope: p.scope,
        });
      case 'timeAway':
        return domiciliaryScheduleService.createTimeAway({
          employee_id: p.employee_id,
          start_date: p.start_date,
          end_date: p.end_date,
          // Both: the canonical type for the leave records, the label for display.
          leave_type: p.leave_type,
          reason: p.reason,
          notes: p.notes,
        });
      case 'removeTimeAway':
        return domiciliaryScheduleService.deleteTimeAway(p.existingId);
      case 'availability':
        return domiciliaryScheduleService.createAvailability({
          employee_id: p.employee_id, zone_id: p.zone_id || null, date: p.date, start_time: p.start, end_time: p.end, note: p.note,
        });
      case 'stopVisit':
        return domiciliaryScheduleService.stopFromDate({ assignment_id: p.assignment_id, date: p.date });
      case 'review':
        return domiciliaryScheduleService.recordVisitHours({
          assignment_id: p.assignment_id, employee_id: p.employee_id, visit_date: p.date,
          clock_in: p.clock_in, clock_out: p.clock_out, note: p.note,
        });
      default:
        return Promise.resolve();
    }
  };

  // ── Review system (mirrors the duty roster) ───────────────────────────────
  const openReviewList = (zone) => setReviewScope({ zone: zone || null });
  const openReview = (item) => { setReviewScope(null); setReviewItem(item); };
  // Direct review of one specific visit (the small "Review" button on the card).
  const openReviewForVisit = (zone, carer, visit) => {
    const kind = visitReviewKind(visit);
    if (!kind) return;
    setReviewScope(null);
    setReviewItem({ zone, carer, visit, kind });
  };

  // Resolve a review item by recording / correcting the actual worked time.
  const submitReview = (clockIn, clockOut, note) => {
    if (!reviewItem) return;
    if (!clockIn || !clockOut) { toast.error('Enter both clock-in and clock-out'); return; }
    const iso = (hm) => { const d = new Date(`${date}T${hm}:00`); return Number.isNaN(d.getTime()) ? null : d.toISOString(); };
    const it = reviewItem;
    enqueue({
      type: 'review',
      dedupKey: `review:${it.visit.assignment_id}:${it.carer.id}:${date}`,
      payload: { assignment_id: it.visit.assignment_id, employee_id: it.carer.id, date, clock_in: iso(clockIn), clock_out: iso(clockOut), note: note || null },
      label: `Hours · ${it.carer.name} · ${it.visit.resident?.name || it.visit.package_name} (${clockIn}–${clockOut})`,
    });
    toast.success('Hours added to draft');
    setReviewItem(null);
  };

  // "Edit times" from Visit Details — correct hours that were already approved
  // / published. Same 'review' draft as submitReview (same dedupKey, so it
  // replaces an unpublished review of that visit); the API appends it to the
  // visit's change log on Publish. A reason is required.
  const stageHoursEdit = ({ visit, carer }, clockIn, clockOut, note) => {
    if (!clockIn || !clockOut) { toast.error('Enter both clock-in and clock-out'); return false; }
    if (!String(note || '').trim()) { toast.error('Add a reason for the change'); return false; }
    const iso = (hm) => { const d = new Date(`${date}T${hm}:00`); return Number.isNaN(d.getTime()) ? null : d.toISOString(); };
    enqueue({
      type: 'review',
      dedupKey: `review:${visit.assignment_id}:${carer.id}:${date}`,
      payload: { assignment_id: visit.assignment_id, employee_id: carer.id, date, clock_in: iso(clockIn), clock_out: iso(clockOut), note: note.trim() },
      label: `Hours edited · ${carer.name} · ${visit.resident?.name || visit.package_name || 'Visit'} (${clockIn}–${clockOut})`,
    });
    toast.success('Time change added to draft — publish to save');
    setDetailVisit(null);
    return true;
  };

  // Resolve a missed item by marking the carer absent (reuses time-away staging).
  const markAwayFromReview = (leaveType, note, label) => {
    if (!reviewItem) return;
    stageTimeAway({
      employee_id: reviewItem.carer.id,
      name: reviewItem.carer.name,
      leave_type: leaveType,
      reason: label || 'Other',
      notes: note,
      existingId: null,
    });
    toast.success('Absence added to draft');
    setReviewItem(null);
  };

  const reviewListItems = reviewScope
    ? (reviewScope.zone ? collectReviews([reviewScope.zone]) : reviews)
    : [];

  // Carer live status (clock in/out, break, travel) — fetched on demand.
  const openCarerStatus = async (carer) => {
    setCarerStatus({ carer, loading: true, data: null });
    try {
      const res = await domiciliaryScheduleService.getCarerStatus(carer.id, date);
      setCarerStatus({ carer, loading: false, data: res.success ? res.data : null });
    } catch (e) {
      setCarerStatus({ carer, loading: false, data: null });
    }
  };

  // Publish = sync the whole draft queue, in order. Failed changes stay queued
  // so nothing is lost; then notify carers and reload the saved snapshot.
  const handlePublish = async () => {
    if (changes.length === 0) { toast('No changes to publish'); return; }
    setPublishing(true);
    const total = changes.length;
    const failed = [];
    for (const c of changes) {
      try {
        const res = await syncChange(c);
        if (res && res.success === false) failed.push(c);
      } catch (err) {
        failed.push(c);
      }
    }
    try { await domiciliaryScheduleService.publishSchedule(); } catch (_) { /* notify is best-effort */ }

    if (failed.length === 0) {
      dispatch(clearChanges());
      toast.success(`Published ${total} change${total === 1 ? '' : 's'} to carers`);
      setPublishOpen(false);
    } else {
      dispatch(setChanges(failed));
      toast.error(`${total - failed.length} of ${total} published — ${failed.length} failed and stay in draft`);
    }
    await fetchSchedule(date);
    setPublishing(false);
  };

  // Warn before leaving with unpublished changes (drafts live only in memory).
  useEffect(() => {
    if (changes.length === 0) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [changes.length]);

  // Open the Time-away modal prefilled to edit/delete a carer's leave.
  const openLeaveEditor = (carer) => {
    if (!carer?.time_away) return;
    setTimeAwayEdit({
      existingId: carer.time_away.id || null,
      employee_id: carer.id,
      name: carer.name,
      // Whatever the row carries — a type, a label, or an older word — the
      // modal resolves it back to one of the three leave types.
      leave_type: carer.time_away.leave_type || null,
      reason: carer.time_away.reason || '',
      notes: carer.time_away.notes || '',
      date,
      isDraft: !!carer.time_away.__draft,
    });
  };

  // Open the Add-availability modal prefilled for a carer (from a green shift
  // band click, or from the time-away editor's "Add availability" action).
  // `window` ({start,end}) prefills the time fields from the clicked timeline
  // band (the carer's shift or an existing extra-availability block).
  const openAvailabilityFor = (carerId, window) => {
    setAvailabilityCarerId(carerId || null);
    setAvailabilityWindow(window && window.start ? { start: window.start, end: window.end } : null);
    setTimeAwayOpen(false);
    setTimeAwayEdit(null);
    setAvailabilityOpen(true);
  };

  const shiftWeek = (deltaDays) => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + deltaDays);
    setDate(localDateKey(dt));
  };

  const a = weekDates[0];
  const b = weekDates[6];
  const weekLabel = a && b
    ? `${MON[a.getMonth()]} ${a.getDate()} – ${a.getMonth() === b.getMonth() ? '' : MON[b.getMonth()] + ' '}${b.getDate()}`
    : '';
  const isToday = date === todayKey();
  const summary = viewData?.summary || {};
  const pendingCount = changes.length;
  // "THU 18 Jun" — label for the fullscreen date bar.
  const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dParts = date.split('-').map(Number);
  const dDate = new Date(dParts[0], dParts[1] - 1, dParts[2]);
  const dateLabelFull = `${DOW3[dDate.getDay()].toUpperCase()} ${dParts[2]} ${MON[dParts[1] - 1]}`;

  // Zone filter (client-side over the loaded day).
  const shownZones = useMemo(() => {
    const zs = viewData?.zones || [];
    return zoneFilter === 'all' ? zs : zs.filter((z) => String(z.id) === String(zoneFilter));
  }, [viewData, zoneFilter]);

  if (loading && !data) return <ScheduleSkeleton />;

  // The Daily schedule tools row — rendered in the sticky header (normal view)
  // AND inside the fullscreen overlay so it stays available there too.
  const dailyToolbar = (
    <ScheduleToolbar
      summary={summary}
      zones={viewData?.zones || []}
      unassigned={viewData?.unassigned || []}
      reviewCount={reviews.length}
      onReview={() => openReviewList(null)}
      onCreateZone={() => setZoneModal({})}
      onAvailability={() => { setAvailabilityCarerId(null); setAvailabilityWindow(null); setAvailabilityOpen(true); }}
      onTimeAway={() => { setTimeAwayEdit(null); setTimeAwayOpen(true); }}
    />
  );

  return (
    <div className="flex h-full flex-col font-sans">
      {/* Sticky header (incl. the Daily schedule row) — stays pinned whether the
          timeline scrolls internally or (when zoomed) the page scrolls. */}
      <div className="sticky top-0 z-40 flex-shrink-0 bg-[#EEF2F6] dark:bg-[#1b1f26] -mx-6 md:-mx-8 -mt-6 md:-mt-8 px-6 md:px-8 pt-3 md:pt-4 pb-2.5 border-b border-[#E2E8F0] dark:border-gray-800">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-5 flex-wrap">
            <div>
              <Heading level={2} className="leading-tight">Schedule</Heading>
              <Text variant="caption" as="p">Plan visits by zone, carer and travel area</Text>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => shiftWeek(-7)} className="p-1 rounded-md hover:bg-black/5 transition text-[#64748B] dark:text-gray-400">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-[14px] font-medium text-[#334155] dark:text-gray-200">Week of {weekLabel}</span>
              <button onClick={() => shiftWeek(7)} className="p-1 rounded-md hover:bg-black/5 transition text-[#64748B] dark:text-gray-400">
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => setDate(todayKey())}
                disabled={isToday}
                className={`ml-1 inline-flex items-center px-3 py-1.5 rounded-xl text-[13px] font-bold transition shadow-sm ${
                  isToday
                    ? 'bg-white dark:bg-[#31373f] text-[#94A3B8] dark:text-gray-500 border border-[#E2E8F0] cursor-not-allowed'
                    : 'bg-white dark:bg-[#31373f] text-[#2563EB] dark:text-blue-400 border border-[#2563EB] hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                Today
              </button>
            </div>
          </div>

          {/* Top-right: zone filter, navigation, export, publish */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <Select
              className="w-[150px]"
              value={zoneFilter}
              onChange={setZoneFilter}
              options={[
                { value: 'all', label: 'All zones' },
                ...(viewData?.zones || []).filter((z) => z.id != null).map((z) => ({ value: String(z.id), label: z.name })),
              ]}
            />
            <Button variant="outline" leftIcon={<LayoutGrid className="w-4 h-4" />} onClick={() => navigate('/manager/care-packages')}>
              Care Packages
            </Button>
            <Button variant="outline" leftIcon={<Download className="w-4 h-4" />} onClick={() => setExportOpen(true)} title="Export payroll-ready worked hours">
              Export
            </Button>
            <Button
              variant="primary"
              leftIcon={<UploadCloud className="w-4 h-4" />}
              loading={publishing}
              disabled={pendingCount === 0}
              onClick={() => setPublishOpen(true)}
              title={pendingCount === 0 ? 'No unpublished changes' : `Review & publish ${pendingCount} change(s)`}
            >
              {pendingCount > 0 ? `Publish (${pendingCount})` : 'Publish'}
            </Button>
          </div>
        </div>

        {/* Day pills */}
        <DateSelector weekDates={weekDates} date={date} onChange={setDate} />

        {/* Daily-schedule tools row — inside the sticky header so it stays
            visible when zoomed in / the page scrolls. */}
        <div className="mt-2.5">{dailyToolbar}</div>
      </div>

      {/* Board — fills remaining height; the timeline scrolls inside it. Never
          shorter than ~2 rows so it stays usable on a laptop (page scrolls if
          the screen is too short, with the sticky header staying put). */}
      <div className="flex min-h-[620px] flex-1 flex-col gap-3 pt-3">
        <ZoneRoster
          zones={shownZones}
          date={date}
          win={win}
          dateLabel={dateLabelFull}
          onPrevDay={() => shiftWeek(-1)}
          onNextDay={() => shiftWeek(1)}
          onToday={() => setDate(todayKey())}
          isTodaySelected={isToday}
          unassignedSlot={(
            <UnassignedVisits
              visits={viewData?.unassigned || []}
              onAssign={setAssignVisit}
              zones={viewData?.zones || []}
              date={date}
              onDragVisit={setDragVisit}
              weekCount={summary.unassigned_week || 0}
            />
          )}
          onVisitClick={setDetailVisit}
          onEditZone={setZoneModal}
          onLeaveClick={openLeaveEditor}
          onReview={openReviewList}
          reviewCountOf={reviewCountForZone}
          onCarerDetails={openCarerStatus}
          onReviewVisit={openReviewForVisit}
          onShiftClick={(carer, window) => openAvailabilityFor(carer.id, window || carer.shift || (carer.extra_availability && carer.extra_availability[0]) || null)}
          dragActive={!!dragVisit}
          dragVisit={dragVisit}
          onDropVisit={(carer) => assignByDrop(dragVisit, carer)}
        />
      </div>

      {/* Modals */}
      <AssignPackageModal
        visit={assignVisit}
        date={date}
        onClose={() => setAssignVisit(null)}
        stageAssign={stageAssign}
      />
      <VisitDetailsModal
        visit={detailVisit}
        date={date}
        onClose={() => setDetailVisit(null)}
        stageStop={stageStop}
        stageUnassign={stageUnassign}
        stageHoursEdit={stageHoursEdit}
        onEditFromDay={(id) => { setDetailVisit(null); setFormState({ packageId: id, fromDate: date }); }}
      />
      {formState && (
        <PackageFormModal
          key={`${formState.packageId}-${formState.fromDate}`}
          open
          packageId={formState.packageId}
          fromDate={formState.fromDate}
          zones={(viewData?.zones || []).filter((z) => z.id != null).map((z) => ({ id: z.id, name: z.name, color: z.color, postcode_prefixes: z.postcode_prefixes }))}
          carers={carers}
          defaultDate={date}
          onClose={() => setFormState(null)}
          onSaved={() => { setFormState(null); refresh(); }}
        />
      )}
      {zoneModal && (
        <CreateZoneModal
          key={zoneModal.id || 'new-zone'}
          open
          zone={zoneModal}
          carers={carers}
          zones={viewData?.zones || []}
          onClose={() => setZoneModal(null)}
          onSaved={() => { setZoneModal(null); refresh(); }}
        />
      )}
      {(timeAwayOpen || timeAwayEdit) && (
        <TimeAwayModal
          open
          carers={carers}
          defaultDate={date}
          editAway={timeAwayEdit}
          onStage={stageTimeAway}
          onDelete={stageRemoveTimeAway}
          onAddAvailability={openAvailabilityFor}
          onClose={() => { setTimeAwayOpen(false); setTimeAwayEdit(null); }}
        />
      )}
      {availabilityOpen && (
        <AvailabilityModal
          open
          carers={carers}
          zones={viewData?.zones || []}
          defaultDate={date}
          defaultEmployeeId={availabilityCarerId}
          defaultStart={availabilityWindow?.start}
          defaultEnd={availabilityWindow?.end}
          onStage={stageAvailability}
          onClose={() => { setAvailabilityOpen(false); setAvailabilityCarerId(null); setAvailabilityWindow(null); }}
        />
      )}
      {exportOpen && (
        <ExportPayrollModal open date={date} carers={carers} onClose={() => setExportOpen(false)} />
      )}
      <PublishReviewModal
        open={publishOpen}
        changes={changes}
        publishing={publishing}
        onPublish={handlePublish}
        onRemove={removeOneChange}
        onClose={() => setPublishOpen(false)}
      />
      <ReviewListModal
        open={!!reviewScope}
        items={reviewListItems}
        scopeName={reviewScope?.zone?.name}
        onPick={openReview}
        onClose={() => setReviewScope(null)}
      />
      <VisitReviewModal
        key={reviewItem ? `${reviewItem.visit.assignment_id}-${reviewItem.carer.id}` : 'none'}
        item={reviewItem}
        onClose={() => setReviewItem(null)}
        onRecordHours={submitReview}
        onMarkAway={markAwayFromReview}
      />
      <CarerStatusModal
        open={!!carerStatus}
        carer={carerStatus?.carer}
        date={date}
        loading={carerStatus?.loading}
        data={carerStatus?.data}
        onClose={() => setCarerStatus(null)}
      />
    </div>
  );
};

export default ScheduleScreen;
